'use client';

import React, { useEffect } from 'react';

interface GoogleAnalyticsProps {
  measurementId: string;
}

// We don't need to redeclare these since they're already in global.d.ts
// Just using the existing definitions

export function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  // Don't render anything if no measurement ID is provided
  if (!measurementId || measurementId.trim() === '') {
    return null;
  }

  // Check if we're in development mode
  const isDevelopment = import.meta.env.MODE === 'development' || 
                       window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
  
  useEffect(() => {
    // This ensures the script only runs on the client-side
    const addGAScript = () => {
      try {
        // In development, log instead of loading actual script if GTM is blocked
        if (isDevelopment) {
          console.log('🔧 Development mode: Google Analytics would be loaded with ID:', measurementId);
          console.log('🔧 To test GA in dev, ensure www.googletagmanager.com is accessible');
          
          // Create mock gtag for development testing
          window.dataLayer = window.dataLayer || [];
          window.gtag = function(...args: any[]) {
            console.log('🔧 Mock GA event:', args);
            if (window.dataLayer) {
              window.dataLayer.push(args);
            }
          };
          
          // Mock the config call
          window.gtag('js', new Date());
          window.gtag('config', measurementId);
          return;
        }

        // Check if script already exists to prevent duplicates
        const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
        if (existingScript) {
          console.log('Google Analytics script already loaded');
          return;
        }

        // Create the script element
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        script.async = true;
        
        // Add error handling
        script.onerror = (error) => {
          console.error('Failed to load Google Analytics script:', error);
          console.error('This might be due to ad blockers or network restrictions');
        };
        
        script.onload = () => {
          console.log('Google Analytics script loaded successfully');
        };
        
        document.head.appendChild(script);
        
        // Set up the dataLayer and gtag function
        window.dataLayer = window.dataLayer || [];
        
        // Implement gtag exactly as Google Analytics does
        window.gtag = function() {
          if (window.dataLayer) {
            window.dataLayer.push(arguments);
          }
        };
        
        // Initialize with standard commands
        window.gtag('js', new Date());
        window.gtag('config', measurementId);
      } catch (error) {
        console.error('Error initializing Google Analytics:', error);
      }
    };
    
    // Run the function to add the script
    addGAScript();
    
    // Cleanup function
    return () => {
      if (!isDevelopment) {
        // Remove the script when the component unmounts (if needed)
        const scripts = document.querySelectorAll(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
        scripts.forEach(script => script.remove());
        
        // Clean up the window properties (only if no other instances are using them)
        if (scripts.length > 0) {
          delete window.gtag;
          delete window.dataLayer;
        }
      }
    };
  }, [measurementId, isDevelopment]);
  
  return null; // The script is added programmatically, so we don't need to render anything
} 